// GENERATED FILE -- do not edit by hand.
// Regenerate with `npm run codegen:stores` (see frontend/package.json).
// Source: agent.app.agent.agent_session.AgentSession

import { makeObservable, observable } from "mobx";
import { applyJsonPatch, JsonPatchOp } from "@/app_store/rpc/json_patch";
import { AgentTransport, callRpc } from "@/app_store/rpc/rpc";
import { registerEntity, wrapEntity } from "@/app_store/rpc/entity";
import { TabSettingsData } from "./TabSettings.generated";
import { PromptData, PromptStore } from "./Prompt.generated";
import { PromptEventData, PromptEventStore } from "./PromptEvent.generated";
import { AgentOptionData } from "./AgentOption.generated";

export interface AgentSessionData {
    // Stamped onto every @observable model's dump at runtime (see
    // common._ObservableMixin._serialize_with_tag) -- tells wrapEntity which
    // generated Store class this value belongs to. Never sent by a client.
    _entity_class_name: "AgentSession";
    id: string;
    name: string | null;
    tab_settings: TabSettingsData;
    history: PromptData[];
    flat_history: PromptEventData[];
    is_streaming: boolean;
    thinking_dots: number;
    available_backends: AgentOptionData[];
    available_models: AgentOptionData[];
    available_efforts: AgentOptionData[];
    current_model: string | null;
    current_effort: string | null;
    options_loading: boolean;
    options_error: string | null;
}

export class AgentSessionStore {
    readonly _entity_class_name = "AgentSession" as const;
    id: string;
    name: string | null;
    tab_settings: TabSettingsData;
    history: PromptStore[];
    flat_history: PromptEventStore[];
    is_streaming: boolean;
    thinking_dots: number;
    available_backends: AgentOptionData[];
    available_models: AgentOptionData[];
    available_efforts: AgentOptionData[];
    current_model: string | null;
    current_effort: string | null;
    options_loading: boolean;
    options_error: string | null;
    private _transport: AgentTransport;
    private _chainPath: string[];

    constructor(data: AgentSessionData, transport: AgentTransport, chainPath: string[] = []) {
        this._transport = transport;
        this._chainPath = chainPath;
        this.id = wrapEntity(data.id, transport, [...chainPath, "id"]);
        this.name = wrapEntity(data.name, transport, [...chainPath, "name"]) ?? null;
        this.tab_settings = wrapEntity(data.tab_settings, transport, [...chainPath, "tab_settings"]);
        this.history = wrapEntity(data.history, transport, [...chainPath, "history"]);
        this.flat_history = wrapEntity(data.flat_history, transport, [...chainPath, "flat_history"]);
        this.is_streaming = wrapEntity(data.is_streaming, transport, [...chainPath, "is_streaming"]);
        this.thinking_dots = wrapEntity(data.thinking_dots, transport, [...chainPath, "thinking_dots"]);
        this.available_backends = wrapEntity(data.available_backends, transport, [...chainPath, "available_backends"]);
        this.available_models = wrapEntity(data.available_models, transport, [...chainPath, "available_models"]);
        this.available_efforts = wrapEntity(data.available_efforts, transport, [...chainPath, "available_efforts"]);
        this.current_model = wrapEntity(data.current_model, transport, [...chainPath, "current_model"]) ?? null;
        this.current_effort = wrapEntity(data.current_effort, transport, [...chainPath, "current_effort"]) ?? null;
        this.options_loading = wrapEntity(data.options_loading, transport, [...chainPath, "options_loading"]);
        this.options_error = wrapEntity(data.options_error, transport, [...chainPath, "options_error"]) ?? null;
        makeObservable(this, {
            id: observable,
            name: observable,
            tab_settings: observable,
            history: observable,
            flat_history: observable,
            is_streaming: observable,
            thinking_dots: observable,
            available_backends: observable,
            available_models: observable,
            available_efforts: observable,
            current_model: observable,
            current_effort: observable,
            options_loading: observable,
            options_error: observable,
        });
    }

    applyPatch(op: JsonPatchOp): void {
        applyJsonPatch(this, op);
    }

    async load_options(session: any): Promise<any> {
        return callRpc(this._transport, this._chainPath, "load_options", [session], {});
    }

    async update_settings(model?: string | null, effort?: string | null, backend?: string | null, readonly_mode?: boolean | null, draft_prompt?: string | null, position?: number | null): Promise<any> {
        return callRpc(this._transport, this._chainPath, "update_settings", [model, effort, backend, readonly_mode, draft_prompt, position], {});
    }

    async run_prompt(message: string, context_files: string[] | null, session: any): Promise<any> {
        return callRpc(this._transport, this._chainPath, "run_prompt", [message, context_files, session], {});
    }

    async interrupt(): Promise<any> {
        return callRpc(this._transport, this._chainPath, "interrupt", [], {});
    }
}

registerEntity("AgentSession", AgentSessionStore);
